import { Component } from '@theme/component';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLButtonElement} [pauseToggle]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * Observes <body> class changes so slides scoped to an A/B test class
   * (added via JS at runtime) are re-evaluated as soon as the class appears.
   * @type {MutationObserver|undefined}
   */
  #bodyObserver = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // Normalize to the first visible slide (scoped slides may be hidden).
    this.current = 0;

    this.#bodyObserver = new MutationObserver(() => {
      this.current = this.#current;
    });
    this.#bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    // Start paused for reduced-motion visitors, unless the section's "Pause for reduced
    // motion" setting is off. Compared against 'false' so a missing attribute keeps the
    // accessible default. The visitor can always press play.
    const respectReducedMotion = this.dataset.respectReducedMotion !== 'false';

    if (respectReducedMotion && window.matchMedia(REDUCED_MOTION_QUERY).matches) {
      this.pause();
    } else {
      this.play();
    }

    this.#syncToggle();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.suspend();
    this.#bodyObserver?.disconnect();
    this.#bodyObserver = undefined;
  }

  /**
   * Slides that are currently visible (not hidden by A/B test scoping).
   * @returns {HTMLElement[]}
   */
  get visibleSlides() {
    return (this.refs.slides ?? []).filter((slide) => slide.offsetParent !== null);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  /**
   * Flips playback from the pause/play control (WCAG 2.2.2 — auto-moving content
   * lasting more than 5s needs a user-operable pause). Bound via `on:click="/togglePause"`.
   */
  togglePause() {
    if (this.paused) {
      this.play();
    } else {
      this.pause();
    }

    this.#syncToggle();
  }

  /**
   * Mirrors playback state onto the toggle button and the live region.
   *
   * `aria-live` stays "off" while the bar rotates on its own: a live region that
   * re-announces every few seconds talks over the rest of the page indefinitely. Once the
   * visitor pauses, slide changes are theirs to drive, so announcing them is correct.
   */
  #syncToggle() {
    const paused = this.paused;

    this.setAttribute('aria-live', paused ? 'polite' : 'off');

    const toggle = this.refs.pauseToggle;
    if (!toggle) return;

    toggle.setAttribute('aria-pressed', String(paused));

    const label = paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause;
    if (label) toggle.setAttribute('aria-label', label);
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.suspend();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    const visible = this.visibleSlides;

    // Hide every slide, then reveal only the active visible one.
    this.refs.slides?.forEach((slide) => slide.setAttribute('aria-hidden', 'true'));

    if (visible.length === 0) return;

    let relativeIndex = current % visible.length;
    if (relativeIndex < 0) {
      relativeIndex += visible.length;
    }

    visible[relativeIndex]?.setAttribute('aria-hidden', 'false');
  }

  /**
   * Suspend the slideshow while the page is hidden.
   *
   * Uses suspend(), not pause(): pause() records an explicit visitor choice, and `resume()`
   * deliberately refuses to override it. Calling pause() here left the bar stopped for good
   * after a single tab switch, and would also desync the pause/play control.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
